import { Fragment, ButtonHTMLAttributes } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTheme } from "../utils/themeUtils";
import { CancelIcon, QuestionIcon, FirstIcon, SecondIcon, ThirdIcon, FourthIcon, FifthIcon} from "../utils/iconFactory";
import { classNames, ModalProps } from '../utils/utils';

interface HelperButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  iconClassName?: string;
}


export function HelperButton({iconClassName, className, ...props}: HelperButtonProps) {
  return (
    <button className={classNames("z-40 absolute top-[90px] right-0 h-[54px] w-[44px] rounded-l-[50px]",
      "bg-backgroundWhite border-l border-y border-primaryAction text-primaryAction cursor-pointer help-drop-shadow",
      "hover:bg-primaryActionBtnHover hover:text-backgroundWhite hover:border-0 help-hover-drop-shadow",
      className)}
      {...props}
    >
      <QuestionIcon className={classNames("h-[30px] w-[30px] translate-x-[11px] fill-current", iconClassName)} />
    </button>
  )
}


type ContentType = "template" | "edit" | "documents" | "profile"
interface HelperModalProps extends ModalProps {
  modalContentType: ContentType
}

export default function HelperModal({ isVisible, onClose, modalContentType }: HelperModalProps) {
  const { themeString } = useTheme()
  return (
    <Transition.Root show={isVisible} as={Fragment}>
      <Dialog as="div" className={classNames("relative z-40", themeString)} onClose={onClose}>
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed top-[90px] right-0 w-[480px] flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-screen">
                  <ModalContent modalContentType={modalContentType} onClose={onClose} />

                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}


function ModalContent({modalContentType, onClose}: {modalContentType:ContentType, onClose: () => void}) {
  switch (modalContentType) {
    case "template":
      return <TemplateContent onClose={onClose} />;
    case "edit":
      return <EditContent onClose={onClose} />;
    case "documents":
      return <DocumentContent onClose={onClose} />;
    case "profile":
      return <ProfileContent onClose={onClose} />;
    default:
      return <div>Other</div>;
  }
}

interface ContentProps {
  onClose: () => void
}

function TemplateContent({onClose}: ContentProps){
  return (
    <div className="relative bg-textMain border-l border-y border-gray40 text-backgroundWhite rounded-l-[24px] ">
      <button className="absolute top-4 right-4" onClick={onClose}>
        <CancelIcon className="absolute top-0 right-0 h-6 w-6 text-backgroundWhite fill-current" />
      </button>
      <div className="flex flex-col p-6 pb-9 gap-4 text-style-body2">
        <h1 className="pt-4 pb-2 text-center text-style-h3">Így használd az AIDA webalkalmazást</h1>
        <p>Az AIDA marketing asszisztens használata egyszerűbb, mint gondolnád.</p>
        <p>Nincs más teendőd, mint kiválasztani azt a területet, amiben segítségre van szükséged. Miután megadtad a terméknevet, az AIDA  azonnal el is végzi a feladatot.</p>
        <p>Nézd meg az alábbi videót, amiben bemutatjuk, hogy hozhatod ki az AIDA-ból a legtöbbet!</p>
        <div className="">
            <iframe className="h-[212px] w-[432px]" src='https://www.youtube.com/embed/qt40RsFT15M' title='Youtube Video' allowFullScreen></iframe>
        </div>
      </div>
    </div>
  )
}

function EditContent({onClose}: ContentProps){
  return (
    <div className="relative bg-textMain border-l border-y border-gray40 text-backgroundWhite rounded-l-[24px]">
      <button className="absolute top-4 right-8" onClick={onClose}>
        <CancelIcon className="absolute top-0 right-0 h-6 w-6 text-backgroundWhite fill-current" />
      </button>
      <div id='content' style={{ maxHeight: "calc(100vh - 90px)" }} className="flex flex-col p-6 py-10 gap-4 text-style-body2 overflow-y-auto">
        <h1 className="text-center text-style-h3">AIDA szerkesztőt</h1>
        <p>A Szerkesztőben AIDA segítségével tudsz szöveget generálni, illetve korábbi dokumentumjaidat is tudod szerkeszteni.</p>

        <ol className='space-y-4'>
          <li key={"1"} className='flex items-center' >{<FirstIcon className="mr-2 flex-none h-10 w-10"/>} Válaszd ki, hogy milyen segítségre van szükséged.</li>
          <li key={"2"} className='flex items-center' >{<SecondIcon className="mr-2 flex-none h-10 w-10" />} Adj meg egy terméknevet  (pl.: Fiskars 1001424 Metszőolló) és egy rövid leírást róla a pontos eredményhez.</li>
          <li key={"3"} className='flex items-center' >{<ThirdIcon className="mr-2 flex-none h-10 w-10" />} Kattints a “Generálás” gombra és azonnal érkezik a válasz.</li>
          <li key={"4"} className='flex items-center' >{<FourthIcon className="mr-2 flex-none h-10 w-10" />} Vidd át a generált szöveget a szerkesztői felületre a képernyőn megjelenő nyíl gomb segítségével.</li>
          <li key={"5"} className='flex items-center' >{<FifthIcon className="mr-2 flex-none h-10 w-10" />} Nevezd el a dokumentumot. szerkeszd, majd mentsd el.</li>
        </ol>

        <div>
          <p className='text-style-body2-bold'>Szöveg generálás kiválaszott feladathoz:</p>
          <p>A téma leírása mezőben add meg a témádat, amelyhez szöveget szeretnél generáltatni. Minél pontosabb leírást adsz, AIDA annál jobb eredménnyel fog szolgálni. Kerülendő és használandó szavaka, kifejezéseket is megadhatsz.</p>
        </div>

        <div>
          <p className='text-style-body2-bold'>Másikat választok:</p>
          <p>A “Másikat választok” gomb segítségével egy új feladatot/témakört tudsz kiválasztani, amihez szöveget szeretnél generálni.</p>
        </div>

        <div>
          <p className='text-style-body2-bold'>Szöveg szerkesztése:</p>
          <p>A szerkesztői panelben átírhatod és formázhatod a leírást. Amíg nem nevezed el a dokumentumot, addig nem tudod elmenteni.</p>
          <p>Az elmentett dokumentum automatikusan megtalálható lesz a “Dokumentumok” menüpontban.</p>
        </div>

        <div>
          <p className='text-style-body2-bold'>Új létrehozása:</p>
          <p>Kattints erre a gombra, ha egy teljesen új dokumentumot/feladatot szeretnél létrehozni, nem pedig egy meglévőt felülírni.</p>
        </div>

        <div>
          <p className='text-style-body2-bold'>Korábbi dokumentum betöltése:</p>
          <p>Ha egy korábbi dokumentumot szeretnél módosítani, kattints a “Korábbi dokumentum betöltése és szerkesztése” gombra és válaszd ki a szerkesztendő dokumentumot. Változtatások mentése után a dokumentum felülíródik, így a korábbi verziók nem lesznek elérhetők. Ha inkább más néven szeretnéd elmenteni a módosított változatot, nevezd át a dokumentumot.</p>
        </div>

      </div>
    </div>
  )
}

function DocumentContent({onClose}: ContentProps){
  return (
    <div className="relative bg-textMain border-l border-y border-gray40 text-backgroundWhite rounded-l-[24px] ">
      <button className="absolute top-4 right-4" onClick={onClose}>
        <CancelIcon className="absolute top-0 right-0 h-6 w-6 text-backgroundWhite fill-current" />
      </button>
      <div className="flex flex-col p-6 pb-9 gap-4 text-style-body2">
        <h1 className="pt-4 pb-2 text-center text-style-h3">Dokumentumok</h1>
        <p>A dokumentumok olyan tartalmak, amelyeket az egyes feladatok elvégzése után menthetsz el, vagy szerkeszthetsz - akár utólag is. </p>
        <p>Kétféleképpen hozhatsz létre új dokumentumot:
          <ul className='list-disc list-outside ml-[21px]'>
            <li>Elvégeztetsz az AIDA-val egy feladatot, majd elmented.</li>
            <li>Létrehozol egy új, üres dokumentumot, majd ehhez választasz feladatot</li>
          </ul>
        </p>
        <p>A dokumentumokat szerkesztheted, letöltheted vagy másolhatod is a kapcsolódó ikonokra kattintva.</p>
        <p>Törlés esetén a dokumentum nem visszaállítható.</p>
      </div>
    </div>
  )
}

function ProfileContent({onClose}: ContentProps){
  return (
    <div className="relative bg-textMain border-l border-y border-gray40 text-backgroundWhite rounded-l-[24px] ">
      <button className="absolute top-4 right-4" onClick={onClose}>
        <CancelIcon className="absolute top-0 right-0 h-6 w-6 text-backgroundWhite fill-current" />
      </button>
      <div className="flex flex-col p-6 pb-9 gap-4 text-style-body2">
        <h1 className="pt-4 pb-2 text-center text-style-h3">Profil</h1>
        <p>A profilban található adatok a Shoprenter webáruházadban megadott adatok alapján jelennek meg.</p>
        <p>Az előfizetéssel kapcsolatos információkat is megtalálod ebben a menüpontban, amelyet meg tudsz változtatni vagy le tudsz mondani. A lemondást követően nem fogsz tudni hozzáférni bizonyos funkciókhoz.</p>
      </div>
    </div>
  )
}